import { useActionData, useFetcher, useNavigation } from "@remix-run/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ActionData } from "~/lib/help/type";

enum State {
  Wait,
  Fail,
  Success,
}
export const useFetcherResult = <
  T extends any,
  ActionData extends {
    errors?: string[];
    values: { _action: T };
  }
>(options?: {
  key?: string;
}) => {
  const fetcher = useFetcher<ActionData>(options);
  const isLoading = useMemo(() => fetcher.state !== "idle", [fetcher.state]);
  const [fetcherState, setFetcherState] = useState(State.Wait);

  useEffect(() => {
    if (fetcher.state === "idle") {
      setFetcherState(State.Wait);
    }
  }, [fetcher.state]);

  const isSuccess = useMemo(() => fetcher.state === "idle" && fetcherState === State.Success, [fetcher.state, fetcherState]);

  useEffect(() => {
    let isActionReload =
      fetcher.state === "loading" &&
      fetcher.formMethod != null &&
      fetcher.formMethod != "GET" &&
      // If we returned data, we must be reloading
      fetcher.data != null;

    if (isActionReload) {
      if (fetcher?.data?.errors?.length) {
        setFetcherState(State.Fail);
      } else {
        setFetcherState(State.Success);
      }
    }
  }, [fetcher.data, fetcher.data?.errors?.length, fetcher.formMethod, fetcher.state]);

  const error = useMemo(() => {
    if (fetcherState === State.Fail && fetcher.state === "idle") {
      const error = fetcher.data?.errors;
      if (error && error.length) {
        return error;
      }
    }
    return null;
  }, [fetcher.data?.errors, fetcher.state, fetcherState]);

  return { fetcher, error, isSuccess, isLoading };
};
export const useActionResult = <T extends any, A extends ActionData<T>>({ action, _action }: { action: string; _action?: T }) => {
  const navigation = useNavigation();
  const data = useActionData<A>();
  const [fetcherState, setFetcherState] = useState(State.Wait);

  const isLoading = useMemo(
    () => navigation.formAction === action && (_action ? _action === navigation.formData.get("_action") : true),
    [_action, action, navigation.formAction, navigation.formData]
  );

  useEffect(() => {
    if (isLoading) {
      switch (true) {
        case navigation.state === "loading" &&
          navigation.formMethod != null &&
          navigation.formMethod != "GET" &&
          navigation.formAction === navigation.location.pathname: {
          setFetcherState(State.Fail);
          break;
        }
        case navigation.state === "loading" &&
          navigation.formMethod != null &&
          navigation.formMethod != "GET" &&
          navigation.formAction !== navigation.location.pathname: {
          setFetcherState(State.Success);
          break;
        }
      }
    }
  }, [isLoading, navigation, navigation.formAction, navigation.formMethod]);
  const error = useMemo(() => {
    if (fetcherState === State.Fail && navigation.state === "idle") {
      const error = data?.errors;
      if (error && error.length) {
        return error;
      }
    }
    return null;
  }, [data?.errors, fetcherState, navigation.state]);

  const isSuccess = useMemo(
    () => navigation.state === "idle" && fetcherState === State.Success,
    [fetcherState, navigation.state]
  );
  useEffect(() => {
    if (navigation.state === "idle") {
      setFetcherState(State.Wait);
    }
  }, [navigation.state]);
  return { error, isSuccess, isLoading, transition: navigation, data };
};

export const useLoaderResult = <Result, Request extends Record<string, string>>({
  req,
  action,
}: {
  req: Request;
  action: string;
}) => {
  const fetch = useFetcher<Result>();
  const load = useCallback(() => fetch.submit(req, { method: "get", action }), [action, req, fetch]);
  const loading = useMemo(() => fetch.state !== "idle", [fetch.state]);
  const data = useMemo(() => fetch.data, [fetch.data]);
  return { load, loading, data };
};
